import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import {
  ConfirmDeleteDialog,
  StatusBadge,
  courseLabel,
  formatDateTime,
  labelize,
} from '@/components/admin/AcademicTableHelpers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  deleteSubmission,
  getCourses,
  getStudents,
  getSubmissionsPaginated,
  gradeSubmission,
} from '@/lib/api';
import { CheckCircle, ChevronLeft, ChevronRight, FileText, Pencil, Trash2 } from 'lucide-react';

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

function scoreLabel(submission) {
  const score = Number(submission.score || 0);
  const max = Number(submission.max_score || 0);
  const percent = max > 0 ? Math.round((score / max) * 100) : null;
  return `${score}/${max || '-'}${percent === null ? '' : ` (${percent}%)`}`;
}

export default function AdminSubmissions() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 15, total_pages: 0 });
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ type: 'all', status: 'all', course_id: 'all', student_id: 'all', page: 1, limit: 15 });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [gradeDialog, setGradeDialog] = useState(null);
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const loadRequestId = useRef(0);

  const loadSupportData = async () => {
    try {
      const [courseRes, studentRes] = await Promise.all([getCourses(), getStudents()]);
      setCourses(courseRes.data || []);
      setStudents(studentRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Could not load submission filters');
    }
  };

  const loadData = async () => {
    const requestId = ++loadRequestId.current;
    try {
      setLoading(true);
      const params = {
        search: searchQuery || undefined,
        type: filterParam(filters.type),
        status: filterParam(filters.status),
        course_id: filterParam(filters.course_id),
        student_id: filterParam(filters.student_id),
        page: filters.page,
        limit: filters.limit,
      };
      const res = await getSubmissionsPaginated(params);
      if (requestId !== loadRequestId.current) return;
      const nextData = res.data || { items: [], total: 0, page: filters.page, limit: filters.limit, total_pages: 0 };
      setData(nextData);
      setSelectedIds((prev) => {
        const visible = new Set((nextData.items || []).map((submission) => submission.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      if (requestId !== loadRequestId.current) return;
      console.error(err);
      toast.error('Could not load submissions');
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadSupportData();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearchQuery(search.trim()), 300);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery]);

  const courseOptions = useMemo(
    () => [{ value: 'all', label: 'All courses' }, ...courses.map((course) => ({ value: course.id, label: courseLabel(course) }))],
    [courses],
  );

  const studentOptions = useMemo(
    () => [{ value: 'all', label: 'All students' }, ...students.map((student) => ({ value: student.id, label: student.name || student.email }))],
    [students],
  );

  const selectedSubmissions = useMemo(
    () => (data.items || []).filter((submission) => selectedIds.has(submission.id)),
    [data.items, selectedIds],
  );

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const openGrade = (submission) => {
    setGradeDialog(submission);
    setScore(submission.score || 0);
    setFeedback(submission.feedback || '');
  };

  const handleGrade = async () => {
    if (!gradeDialog) return;
    try {
      await gradeSubmission(gradeDialog.id, { score: Number(score) || 0, feedback });
      toast.success('Submission graded');
      setGradeDialog(null);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to grade submission');
    }
  };

  const confirmDelete = (items, type = 'single') => {
    setPendingDelete({ type, submissions: items });
  };

  const refreshAfterDelete = async (deletedCount) => {
    if ((data.items || []).length <= deletedCount && filters.page > 1) {
      setFilters((prev) => ({ ...prev, page: prev.page - 1 }));
      return;
    }
    await loadData();
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      const ids = pendingDelete.submissions.map((submission) => submission.id);
      await Promise.all(ids.map((id) => deleteSubmission(id)));
      toast.success(`${ids.length} submission${ids.length === 1 ? '' : 's'} deleted`);
      setPendingDelete(null);
      setSelectedIds(new Set());
      await refreshAfterDelete(ids.length);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete submissions');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      id: 'student',
      header: 'Student',
      sortable: true,
      sortAccessor: (submission) => submission.student_name || '',
      cellClassName: 'min-w-[230px]',
      cell: (submission) => (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#062B1A]/5 flex items-center justify-center">
            <FileText className="w-4 h-4 text-[#062B1A]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{submission.student_name || 'Unknown student'}</p>
            <p className="text-xs text-[#062B1A]/45">{submission.student_email || 'No email'}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      sortable: true,
      cellClassName: 'min-w-[110px]',
      cell: (submission) => <Badge variant="outline" className="border-[#062B1A]/15 text-[#062B1A]">{labelize(submission.type)}</Badge>,
    },
    {
      id: 'assessment',
      header: 'Assessment',
      sortable: true,
      sortAccessor: (submission) => submission.reference_title || '',
      cellClassName: 'min-w-[240px] text-sm text-[#062B1A]/70',
      cell: (submission) => submission.reference_title || 'Unknown assessment',
    },
    {
      id: 'course',
      header: 'Course',
      sortable: true,
      sortAccessor: (submission) => submission.course_name || '',
      cellClassName: 'min-w-[200px] text-sm text-[#062B1A]/70',
      cell: (submission) => submission.course_name || 'Unknown course',
    },
    {
      id: 'score',
      header: 'Score / Max',
      sortable: true,
      sortAccessor: (submission) => Number(submission.max_score || 0) > 0 ? Number(submission.score || 0) / Number(submission.max_score || 1) : 0,
      cellClassName: 'min-w-[140px] text-sm font-medium text-[#062B1A]',
      cell: scoreLabel,
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[120px]',
      cell: (submission) => <StatusBadge value={submission.status} />,
    },
    {
      id: 'submitted_at',
      header: 'Submitted at',
      sortable: true,
      cellClassName: 'min-w-[170px] text-xs text-[#062B1A]/55',
      cell: (submission) => formatDateTime(submission.submitted_at),
    },
    {
      id: 'graded_at',
      header: 'Graded at',
      sortable: true,
      cellClassName: 'min-w-[170px] text-xs text-[#062B1A]/55',
      cell: (submission) => formatDateTime(submission.graded_at),
    },
  ];

  const rowActions = (submission) => (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        size="sm"
        className={submission.status === 'submitted' ? 'bg-[#D4AF37] text-black hover:bg-[#B8941F]' : 'bg-[#062B1A] text-white hover:bg-[#041B10]'}
        onClick={() => openGrade(submission)}
      >
        {submission.status === 'submitted' ? <CheckCircle className="w-4 h-4 mr-2" /> : <Pencil className="w-4 h-4 mr-2" />}
        {submission.status === 'submitted' ? 'Grade' : 'Edit'}
      </Button>
      <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => confirmDelete([submission])}>
        <Trash2 className="w-4 h-4 mr-2" />
        Delete
      </Button>
    </div>
  );

  const bulkActions = ({ selectedCount }) => (
    <Button
      type="button"
      size="sm"
      disabled={selectedCount === 0}
      className="bg-red-700 text-white hover:bg-red-800"
      onClick={() => confirmDelete(selectedSubmissions, 'bulk')}
    >
      <Trash2 className="w-4 h-4 mr-2" />
      Bulk Delete
    </Button>
  );

  const showingStart = data.total === 0 ? 0 : (data.page - 1) * data.limit + 1;
  const showingEnd = Math.min(data.page * data.limit, data.total);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Submission Review</h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">View, filter, grade, and delete student submissions.</p>
      </div>

      <DataTable
        title="Submission Management"
        description="The list is loaded from the enriched reports endpoint. Row clicks open the grading panel."
        data={data.items || []}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value);
          setFilters((prev) => ({ ...prev, page: 1 }));
        }}
        searchPlaceholder="Search all submissions..."
        searchHint="Search by student name, email or number, assessment title, course title, or course code."
        filters={[
          { id: 'type', value: filters.type, onChange: (value) => updateFilter('type', value), options: [{ value: 'all', label: 'All types' }, { value: 'exam', label: 'Exams' }, { value: 'assignment', label: 'Assignments' }] },
          { id: 'status', value: filters.status, onChange: (value) => updateFilter('status', value), options: [{ value: 'all', label: 'All statuses' }, { value: 'submitted', label: 'Pending' }, { value: 'graded', label: 'Graded' }] },
          { id: 'course', value: filters.course_id, onChange: (value) => updateFilter('course_id', value), options: courseOptions },
          { id: 'student', value: filters.student_id, onChange: (value) => updateFilter('student_id', value), options: studentOptions },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        onRowClick={openGrade}
        emptyMessage="No submissions match the current filters."
        pageSize={filters.limit}
        showPagination={false}
        footerSummary={`Showing ${showingStart} - ${showingEnd} of ${data.total} submissions`}
      />

      <div className="flex items-center justify-between">
        <p className="text-xs text-[#062B1A]/45">Page {data.page || 1} of {Math.max(data.total_pages || 1, 1)}</p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={(data.page || 1) <= 1}
            onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            className="border-[rgba(6,43,26,0.12)]"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={(data.page || 1) >= (data.total_pages || 1)}
            onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
            className="border-[rgba(6,43,26,0.12)]"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      <Dialog open={!!gradeDialog} onOpenChange={(open) => !open && setGradeDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Grade Submission</DialogTitle>
          </DialogHeader>
          {gradeDialog && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-[#F7F8FA]">
                <p className="text-sm font-medium text-[#062B1A]">{gradeDialog.reference_title || 'Untitled assessment'}</p>
                <p className="text-xs text-[#062B1A]/50">Student: {gradeDialog.student_name}</p>
                {gradeDialog.content && <p className="text-xs text-[#062B1A]/60 mt-2 whitespace-pre-wrap">{gradeDialog.content}</p>}
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Score (out of {gradeDialog.max_score})</label>
                <Input type="number" value={score} onChange={(event) => setScore(parseInt(event.target.value, 10) || 0)} max={gradeDialog.max_score} className="border-[rgba(6,43,26,0.12)]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Feedback</label>
                <Textarea value={feedback} onChange={(event) => setFeedback(event.target.value)} placeholder="Optional feedback..." className="border-[rgba(6,43,26,0.12)]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGradeDialog(null)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleGrade}>Submit Grade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.submissions?.length || 0} submission${pendingDelete?.submissions?.length === 1 ? '' : 's'}?`}
        description="This permanently removes the selected submission record. No child records are cascaded."
        count={pendingDelete?.submissions?.length || 0}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
}
